// Achievements as data (decision 0060): each goal is a counter over records (`counter`: the records it counts, by
// kind and fields, or the distinct values of one field), a target, a condition over the game's facts (`when`), or a
// counter and a condition together. An achievement unlocks once, in the order unlocks happen; its progress keeps
// counting after. The state is JSON (`snapshot`/`restore`) and two states merge without a conflict (`mergeAchievements`:
// unlocks are a union, counters the larger, distinct values a union), so a sync never has to ask the player.
import { matches, type FactTest, type Match, type MetaFactSource, type MetaValue } from "./rules";

/** What a counter counts: records of kind `on` whose payload matches; `distinct` counts each value of that field
 *  once; `add` adds that numeric field instead of 1. */
export interface CounterRule {
  on: string;
  match?: Match;
  distinct?: string;
  add?: string;
}

/** One achievement. It needs `counter` with `target`, or `when`, or both (all must hold). */
export interface AchievementDef {
  id: string;
  counter?: CounterRule;
  /** The count that unlocks it (default 1 with a counter). */
  target?: number;
  /** A condition in the game's fact language, checked on `check()`. */
  when?: string;
  /** Hidden until unlocked (the screen shows a placeholder). */
  hidden?: boolean;
}

/** The saved state: unlocks (id → the order it unlocked in, from 1), counters and the distinct values seen. */
export interface AchievementState {
  unlocked: Record<string, number>;
  counters: Record<string, number>;
  seen: Record<string, string[]>;
}

/** One achievement's progress. */
export interface AchievementProgress {
  id: string;
  unlocked: boolean;
  /** Its place in the unlock order (1 = first), or null. */
  order: number | null;
  /** The counter's value (0 without a counter) and the target (null without a counter). */
  value: number;
  target: number | null;
  hidden: boolean;
}

/** Problems with a set of definitions (empty when sound): duplicate ids, nothing to unlock on, bad targets. */
export function checkAchievements(defs: readonly AchievementDef[], test?: (condition: string) => string[]): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const d of defs) {
    if (ids.has(d.id)) out.push(`achievement "${d.id}" is defined twice`);
    ids.add(d.id);
    if (!d.counter && !d.when) out.push(`achievement "${d.id}" has neither a counter nor a condition`);
    if (d.target !== undefined && (!d.counter || !(d.target >= 1))) out.push(`achievement "${d.id}": a target needs a counter and must be at least 1`);
    if (d.when && test) for (const p of test(d.when)) out.push(`achievement "${d.id}": ${p}`);
  }
  return out;
}

/** A fresh state. */
export const emptyAchievements = (): AchievementState => ({ unlocked: {}, counters: {}, seen: {} });

/** Merge two states (a sync): every unlock of either (the first's order kept, the second's after it), each counter
 *  the larger, distinct values the union. */
export function mergeAchievements(a: AchievementState, b: AchievementState): AchievementState {
  const out: AchievementState = structuredClone(a);
  const next = Object.values(out.unlocked).reduce((m, v) => Math.max(m, v), 0);
  const extra = Object.entries(b.unlocked)
    .filter(([id]) => !(id in out.unlocked))
    .sort((x, y) => x[1] - y[1]);
  extra.forEach(([id], i) => (out.unlocked[id] = next + i + 1));
  for (const [id, v] of Object.entries(b.counters)) out.counters[id] = Math.max(out.counters[id] ?? 0, v);
  for (const [id, vs] of Object.entries(b.seen)) {
    const set = new Set([...(out.seen[id] ?? []), ...vs]);
    out.seen[id] = [...set].sort();
    out.counters[id] = Math.max(out.counters[id] ?? 0, set.size);
  }
  return out;
}

/** The achievements: feed records and check conditions; unlocks are reported once each. */
export class Achievements {
  private s: AchievementState = emptyAchievements();
  private readonly byId = new Map<string, AchievementDef>();
  private readonly listeners: ((id: string) => void)[] = [];

  /** `test` evaluates `when` conditions (without it, achievements with one never unlock). */
  constructor(readonly defs: readonly AchievementDef[], private readonly o: { test?: FactTest } = {}) {
    const bad = checkAchievements(defs);
    if (bad.length) throw new Error(`achievements: ${bad.join("; ")}`);
    for (const d of defs) this.byId.set(d.id, d);
  }

  /** Call `fn` with each achievement's id when it unlocks. Returns the remover. */
  onUnlock(fn: (id: string) => void): () => void {
    this.listeners.push(fn);
    return () => void this.listeners.splice(this.listeners.indexOf(fn), 1);
  }

  /** Feed a record: counters move; returns the ids unlocked by it. */
  record(kind: string, data: Readonly<Record<string, MetaValue>> = {}): string[] {
    for (const d of this.defs) {
      const c = d.counter;
      if (!c || c.on !== kind || !matches(data, c.match)) continue;
      if (c.distinct) {
        const v = data[c.distinct];
        if (v === undefined || v === null) continue;
        const seen = (this.s.seen[d.id] ??= []);
        if (seen.includes(String(v))) continue;
        seen.push(String(v));
        seen.sort();
        this.s.counters[d.id] = seen.length;
      } else {
        const add = c.add ? Number(data[c.add] ?? 0) : 1;
        if (!Number.isFinite(add) || add <= 0) continue;
        this.s.counters[d.id] = (this.s.counters[d.id] ?? 0) + add;
      }
    }
    return this.settle(false);
  }

  /** Check every achievement against its counter and condition now; returns the ids unlocked. */
  check(): string[] {
    return this.settle(true);
  }

  /** Whether `id` is unlocked. */
  unlocked(id: string): boolean {
    return id in this.s.unlocked;
  }

  /** One achievement's progress. */
  progress(id: string): AchievementProgress {
    const d = this.byId.get(id);
    if (!d) throw new Error(`achievements: no achievement "${id}"`);
    return {
      id,
      unlocked: this.unlocked(id),
      order: this.s.unlocked[id] ?? null,
      value: this.s.counters[id] ?? 0,
      target: d.counter ? (d.target ?? 1) : null,
      hidden: d.hidden === true,
    };
  }

  /** Every achievement's progress, in the definitions' order. */
  list(): AchievementProgress[] {
    return this.defs.map((d) => this.progress(d.id));
  }

  /** How many are unlocked, of how many. */
  count(): { unlocked: number; total: number } {
    return { unlocked: Object.keys(this.s.unlocked).length, total: this.defs.length };
  }

  /** The state (a copy, JSON-safe). */
  snapshot(): AchievementState {
    return structuredClone(this.s);
  }

  /** Put a saved state back (unknown ids are kept, so a later definition list still sees them). */
  restore(s: AchievementState): void {
    this.s = structuredClone({ ...emptyAchievements(), ...s });
  }

  /** Merge another state into this one (a sync brought it); returns the ids newly unlocked here. */
  merge(other: AchievementState): string[] {
    const before = new Set(Object.keys(this.s.unlocked));
    this.s = mergeAchievements(this.s, other);
    return Object.keys(this.s.unlocked).filter((id) => !before.has(id));
  }

  /** Facts for the game's condition language: `<name>.<id>` (unlocked), `<name>.count`. */
  facts(name = "achievement"): MetaFactSource {
    return {
      name,
      get: (k) => (k === "count" ? this.count().unlocked : this.unlocked(k)),
      knows: (k) => k === "count" || this.byId.has(k),
      keys: () => ["count", ...this.byId.keys()],
    };
  }

  private settle(conditions: boolean): string[] {
    const out: string[] = [];
    for (const d of this.defs) {
      if (this.unlocked(d.id)) continue;
      if (d.counter && (this.s.counters[d.id] ?? 0) < (d.target ?? 1)) continue;
      if (d.when) {
        if (!conditions || !this.o.test) continue;
        let ok = false;
        try {
          ok = this.o.test(d.when);
        } catch {
          ok = false;
        }
        if (!ok) continue;
      }
      this.s.unlocked[d.id] = Object.keys(this.s.unlocked).length + 1;
      out.push(d.id);
    }
    for (const id of out) for (const l of [...this.listeners]) l(id);
    return out;
  }
}
